import React, { memo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import { useLocale } from '../i18n/LocaleContext';
import { useVisibleInterval } from '../hooks/useVisibleInterval';
import { subscribeDuel } from '../utils/duelStore';

/**
 * Полоска дуэли поверх меню.
 *
 * Дуэль идёт, пока игрок тапает на главном экране, — а счёт при этом живёт на
 * экране дуэлей. Без этой полоски игрок либо сидит в арене и не тапает, либо
 * тапает и не видит, что происходит. Полоска решает обе задачи: она всегда
 * перед глазами и всегда в один тап от арены.
 *
 * Здесь же висит входящий вызов: это самая ценная строчка в интерфейсе — она
 * сообщает, что кто-то ждёт именно тебя.
 */

const fmt = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '0';
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  return n.toFixed(n >= 100 ? 0 : n >= 10 ? 1 : 2);
};

const mmss = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const DuelHud = memo(function DuelHud() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [left, setLeft] = useState(0);

  useEffect(() => subscribeDuel((next) => setState(next)), []);

  const active = state?.active;
  const endsAt = active?.ends_at;

  const tick = useCallback(() => {
    if (!endsAt) return;
    setLeft(Math.max(0, (new Date(endsAt).getTime() - Date.now()) / 1000));
  }, [endsAt]);

  useEffect(tick, [tick]);
  // Отсчёт считается от ends_at, поэтому пауза в фоне ничего не ломает —
  // при возврате хук сразу дёргает tick и значение догоняет реальное время.
  useVisibleInterval(tick, endsAt ? 1000 : null);

  if (pathname === '/clicker/duel') return null;

  // На главной нижнюю полосу занимает панель энергии (fixed, nav-h + 12px,
  // высотой около 78px). Полоску боя поднимаем над ней, иначе она уезжает
  // под панель и видно только её край.
  const overEnergy = pathname === '/clicker';

  if (active) {
    const mine = parseFloat(active.me.score) || 0;
    const theirs = parseFloat(active.foe.score) || 0;
    const total = mine + theirs;
    const share = total > 0 ? (mine / total) * 100 : 50;
    const lead = mine - theirs;
    return (
      <button
        type="button"
        className={`duel-hud ${overEnergy ? 'is-over-energy' : ''} ${lead > 0 ? 'is-ahead' : lead < 0 ? 'is-behind' : ''}`}
        onClick={() => navigate('/clicker/duel')}
      >
        <span className="duel-hud-side">
          <Avatar
            userData={{ photo_url: active.me.photo_url, first_name: active.me.name }}
            size={26}
          />
          <b>{fmt(active.me.score)}</b>
        </span>
        <span className="duel-hud-mid">
          <span className="duel-hud-timer">{mmss(left)}</span>
          <span className="duel-hud-bar" aria-hidden>
            <i style={{ width: `${share}%` }} />
          </span>
          <span className="duel-hud-label">
            {lead > 0 ? t('duelAheadShort') : lead < 0 ? t('duelBehindShort') : t('duelEvenShort')}
          </span>
        </span>
        <span className="duel-hud-side is-foe">
          <b>{fmt(active.foe.score)}</b>
          <Avatar
            userData={{ photo_url: active.foe.photo_url, first_name: active.foe.name }}
            size={26}
          />
        </span>
      </button>
    );
  }

  const incoming = state?.incoming?.[0];
  if (incoming) {
    return (
      <button
        type="button"
        className={`duel-hud is-invite ${overEnergy ? 'is-over-energy' : ''}`}
        onClick={() => navigate('/clicker/duel')}
      >
        <span className="duel-hud-side">
          <Avatar
            userData={{ photo_url: incoming.foe?.photo_url, first_name: incoming.foe?.name }}
            size={26}
          />
        </span>
        <span className="duel-hud-invite-text">
          <b>{t('duelInviteTitle').replace('{name}', incoming.foe?.name || '')}</b>
          <span>
            {incoming.format_label} · {fmt(incoming.stake_amount)}
          </span>
        </span>
        <span className="duel-hud-go">{t('duelOpen')}</span>
      </button>
    );
  }

  return null;
});

export default DuelHud;
